import { createContext, useContext, useEffect, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { useAuth } from '../hooks/useAuth';
import { useProfile } from '../hooks/useProfile';

type AuthContextType = {
  session: Session | null;
  profile: any;
  loading: boolean;
  signIn: (credentials: { email: string; password: string }) => Promise<void>;
  signUp: (credentials: { email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithProvider: (provider: 'apple' | 'google') => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { session, loading: authLoading, signIn, signUp, signOut, signInWithProvider } = useAuth();
  const { profile, loading: profileLoading } = useProfile(session?.user?.id);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !profileLoading) {
      setLoading(false);
    }
  }, [authLoading, profileLoading]);

  const value = {
    session,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithProvider,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
} 